import g from '#core/global';
import load from '#core/load';


const dj_$ = '.donor-journey';
const anchors_$ = '.donor-journey__anchors';
const anchor_$ = '.donor-journey__anchor';
const panel_$ = '.donor-journey__panel';
const trigger_attr = 'data-donor-journey-trigger';
const item_attr = 'data-donor-journey-item';
const active_attr = 'data-donor-journey-active';
const count_attr = 'data-donor-journey-count';
const rotation_attr = 'data-donor-journey-rotation';
const open_attr = 'data-is-open';
const init_attr = 'data-donor-journey-init';

let has_donor_journey = false;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('donor-journey')) return false;

		load.content(() => {
			has_donor_journey = assign_elements();
			if (!has_donor_journey) return false;
		});
		load.all(() => {
			has_donor_journey = assign_elements();
			if (!has_donor_journey) return false;
		});
	},

};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_donor_journey) return true;

	const $djs = document.querySelectorAll(dj_$);
	if (!$djs.length) return false;

	$djs.forEach(setup_dj);

	// Outside-click to close the mobile popover
	document.addEventListener(g.click_e, e => {
		document.querySelectorAll(`${dj_$} ${anchors_$}[${open_attr}="true"]`).forEach($anchors => {
			if (!$anchors.contains(e.target)) {
				$anchors.setAttribute(open_attr, g.false_s);
			}
		});
	});

	return true;
}


/**
 * Wire up a single donor-journey instance
 * @param {Element} $dj
 */
function setup_dj($dj) {
	if ($dj.getAttribute(init_attr) === g.true_s) return;
	$dj.setAttribute(init_attr, g.true_s);

	// Seed the rotation tracker to match the SSR resting position so
	// the first activation animates from the correct starting angle.
	// Initial active index of 0 means "no item selected" → rotation 0.
	const count = parseInt($dj.getAttribute(count_attr), 10) || 1;
	const initial_active = parseInt($dj.getAttribute(active_attr), 10) || 0;
	const initial_rotation = (360 / count) * initial_active;
	$dj.setAttribute(rotation_attr, String(initial_rotation));

	// Click on an anchor — activate it (and close the mobile popover)
	$dj.addEventListener(g.click_e, e => {
		const $anchor = e.target.closest(anchor_$);
		if (!$anchor || !$dj.contains($anchor)) return;

		const $anchors = $dj.querySelector(anchors_$);
		const is_open = $anchors && $anchors.getAttribute(open_attr) === g.true_s;
		const is_active = $anchor.getAttribute(g.is_active_attr) === g.true_s;

		// Mobile: tapping the active pill toggles the popover
		if (is_active && $anchors && is_mobile($dj)) {
			$anchors.setAttribute(open_attr, is_open ? g.false_s : g.true_s);
			return;
		}

		const index = $anchor.getAttribute(trigger_attr);
		activate($dj, index);

		if ($anchors) {
			$anchors.setAttribute(open_attr, g.false_s);
		}
	});
}


/**
 * Whether the donor-journey is in mobile (single-pill) layout.
 * The .donor-journey__anchor-icon-mobile is only visible at mobile sizes.
 * @param {Element} $dj
 * @return {boolean}
 */
function is_mobile($dj) {
	const $icon = $dj.querySelector(
		`${anchor_$}[${g.is_active_attr}="${g.true_s}"] .donor-journey__anchor-icon-mobile`
	);
	if (!$icon) return false;
	return getComputedStyle($icon).display !== 'none';
}


/**
 * Activate the item at the given 1-based index.
 * @param {Element} $dj
 * @param {string|number} index
 */
function activate($dj, index) {
	const idx = String(index);

	$dj.setAttribute(active_attr, idx);
	$dj.style.setProperty('--donor-journey-active', idx);

	update_rotation($dj, parseInt(idx, 10));

	$dj.querySelectorAll(anchor_$).forEach($anchor => {
		const is_match = $anchor.getAttribute(trigger_attr) === idx;
		$anchor.setAttribute(g.is_active_attr, is_match ? g.true_s : g.false_s);
		$anchor.setAttribute('aria-selected', is_match ? g.true_s : g.false_s);
	});

	$dj.querySelectorAll(panel_$).forEach($panel => {
		const is_match = $panel.getAttribute(item_attr) === idx;
		$panel.setAttribute(g.is_active_attr, is_match ? g.true_s : g.false_s);
		$panel.setAttribute('aria-hidden', is_match ? g.false_s : g.true_s);
	});
}


/**
 * Rotate the rings to the active item's position via the shortest path.
 *
 * The "resting" angle for item N is `step * N` (so item 1 sits at one
 * step clockwise from 0, never at 0 itself). We track the cumulative
 * absolute rotation on the element and add the smallest signed delta
 * (in [-180, 180]) needed to reach the new resting angle modulo 360.
 * This means clicking through the list in order keeps spinning
 * clockwise (even past 360deg), while jumping across the list takes
 * whichever direction is shorter.
 *
 * @param {Element} $dj
 * @param {number} idx 1-based active index
 */
function update_rotation($dj, idx) {
	const count = parseInt($dj.getAttribute(count_attr), 10) || 1;
	const step = 360 / count;
	const target_base = step * idx;

	let current = parseFloat($dj.getAttribute(rotation_attr));
	if (Number.isNaN(current)) current = target_base;

	// Smallest signed delta to reach target_base (mod 360), in [-180, 180).
	const delta = ((target_base - current) % 360 + 540) % 360 - 180;
	const next = current + delta;

	$dj.setAttribute(rotation_attr, String(next));
	$dj.style.setProperty('--donor-journey-rotation', `${next}deg`);
}
